import { useCallback, useEffect, useRef, useState } from 'react';
import { getInventoryDashboard } from '../stock/public-api';
import type { InventoryDashboardRow } from '../stock/public-api';
import { isDatabaseUnavailableError } from '../../shared/localDb';
export type InventoryKpis = {
  dashboard: InventoryDashboardRow | null;
  loading: boolean;
  /** True si el último refresh falló — la UI muestra «—»/aviso, nunca ceros falsos. */
  error: boolean;
  refresh: () => Promise<void>;
};

type KpiSnapshotState = { dashboard: InventoryDashboardRow | null; error: boolean };

/**
 * Reduce puro del refresh de KPIs — invariante testeable sin React:
 * un fallo NUNCA pisa el último snapshot válido ni devuelve ceros falsos.
 */
export const reduceInventoryKpiRefresh = (
  prev: KpiSnapshotState,
  outcome:
    | { kind: 'success'; dashboard: InventoryDashboardRow }
    | { kind: 'failure' },
): KpiSnapshotState =>
  outcome.kind === 'success'
    ? { dashboard: outcome.dashboard, error: false }
    : { dashboard: prev.dashboard, error: true };

/**
 * KPIs globales del inventario de la sucursal — independientes de los filtros del listado.
 *
 * Se cargan al montar y cuando cambia la sucursal. Se refrescan:
 * - Manualmente vía `refresh()` tras mutaciones locales (alta, ajuste, transferencia, etc.)
 *
 * NO se recalculan al filtrar/buscar — son métricas del stock completo de la sucursal.
 *
 * Ante un error se conserva el último snapshot válido (patrón useCatalogKpis):
 * `dashboard === null` = nunca cargó → tiles «—»; `error` = el último refresh falló.
 */
export function useInventoryKpis(currentBranchId: string | null | undefined): InventoryKpis {
  const [state, setState] = useState<KpiSnapshotState>({ dashboard: null, error: false });
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);
  const generationRef = useRef(0);
  const branchRef = useRef<string | null | undefined>(currentBranchId);

  const refresh = useCallback(async () => {
    const branchId = branchRef.current;
    if (!branchId) {
      setState({ dashboard: null, error: false });
      setLoading(false);
      return;
    }
    const gen = ++generationRef.current;
    setLoading(true);
    try {
      const data = await getInventoryDashboard(branchId);
      if (gen !== generationRef.current || !mountedRef.current) return;
      setState((prev) => reduceInventoryKpiRefresh(prev, { kind: 'success', dashboard: data }));
    } catch (error) {
      if (!isDatabaseUnavailableError(error)) {
        console.warn('[InventoryKpis] No se pudieron cargar los KPIs', error);
      }
      // No pisar el último snapshot válido con null → la UI decide «—» o stale + retry.
      if (gen === generationRef.current && mountedRef.current) {
        setState((prev) => reduceInventoryKpiRefresh(prev, { kind: 'failure' }));
      }
    } finally {
      if (gen === generationRef.current && mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    branchRef.current = currentBranchId;
    void refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [currentBranchId, refresh]);

  return {
    dashboard: state.dashboard,
    loading,
    error: state.error,
    refresh,
  };
}
